import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import { STRINGS } from '@/strings/strings';
import { AudioStatusPlaceholder } from '@/components/story/AudioStatusPlaceholder';
import { AudioTitleMeta } from '@/components/audio/shared/AudioTitleMeta';
import { AudioProgressBar } from '@/components/audio/shared/AudioProgressBar';
import { AudioBigControls } from '@/components/audio/shared/AudioBigControls';
import { AudioMiniControls } from '@/components/audio/shared/AudioMiniControls';
import { WORLD_NAME_BY_SLUG, formatDate } from '@/components/audio/shared/helpers';
import { SchlafTimerSheet } from '@/components/audio/nowplaying/SchlafTimerSheet';
import { useAudioStore } from '@/stores/audioStore';
import type { StoryDetail } from '@/features/story/types';
import type { WorldSlug } from '@/lib/worlds';

interface Props {
  story: StoryDetail;
  worldSlug: WorldSlug;
}

/**
 * Story-Page audio block. Three visual modes driven by audioStore + story
 * status:
 *
 *   1) status !== 'completed'  → delegate to <AudioStatusPlaceholder>.
 *   2) status === 'completed', not loaded into store yet  → "Audio anhören"
 *      CTA + "mp3 herunterladen" link. URL was pre-fetched on mount so the
 *      tap handler can call audio.play() synchronously (iOS Safari gesture
 *      constraint).
 *   3) status === 'completed', loaded into store (this story)  → full
 *      inline player matching Sönkes `audio-player-inline` version-02
 *      Figma frame (wrapper 4010:1475). Composed from shared primitives
 *      in `components/audio/shared/` — this file is now just a thin
 *      composition layer around the State-A/-B branches.
 *
 * Mount-time pre-fetch is conditional on `audioStore.storyId !== story.id`
 * so navigating back to the same story (e.g. from /library) doesn't
 * re-fetch a URL that's already ready.
 */
export function StoryInlineAudioPlayer({ story, worldSlug }: Props) {
  const storeStoryId = useAudioStore((s) => s.storyId);
  const status = useAudioStore((s) => s.status);
  const downloadUrl = useAudioStore((s) => s.downloadUrl);

  const load = useAudioStore((s) => s.load);
  const play = useAudioStore((s) => s.play);

  // Schlaf-Timer bottom-sheet open state. Mounted in the State-3 render branch
  // (audio loaded). Mirrors the pattern in NowPlayingPage so the Schlaf-Timer
  // button is interactive on the inline player too.
  const [sheetOpen, setSheetOpen] = useState(false);

  const isLoadedStory = storeStoryId === story.id;
  const isReady = isLoadedStory && status === 'ready';

  // Pre-fetch URL on mount so the Play click can run a synchronous
  // audio.play() (iOS Safari user-gesture constraint).
  useEffect(() => {
    if (story.audioStatus !== 'completed') return;
    if (isLoadedStory) return;
    void load({
      storyId: story.id,
      title: story.title,
      childId: story.childId,
      childName: story.childName,
      worldSlug,
      totalDurationS: story.audioDurationS ?? 0,
    });
  }, [story, worldSlug, isLoadedStory, load]);

  if (story.audioStatus !== 'completed') {
    return <AudioStatusPlaceholder status={story.audioStatus} />;
  }

  const copy = STRINGS.storyOutput.page.audio;

  // State 2 — ready-but-not-yet-playing (after mount pre-fetch completes).
  if (!isReady || !isLoadedStory) {
    const loading = isLoadedStory && status === 'loading';
    return (
      <div>
        <button
          type="button"
          disabled={loading}
          onClick={() => {
            void play();
          }}
          className={cn(
            'h-11 w-full rounded-lg bg-dream-gold text-sm font-bold text-dream-navy transition hover:bg-dream-gold/90',
            loading && 'opacity-60',
          )}
        >
          {loading ? copy.loading : copy.ready}
        </button>
        {downloadUrl && (
          <a
            href={downloadUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 inline-block w-full text-center text-xs text-dream-navy underline decoration-dream-navy/60 underline-offset-2 hover:text-dream-navy/80"
          >
            {copy.mp3Download}
          </a>
        )}
      </div>
    );
  }

  // State 3 — loaded, playing or paused. Sönkes audio-player-inline v02.
  // SchlafTimerSheet is rendered as a sibling so the Radix portal mounts to
  // document.body cleanly; the player layout stays unchanged.
  return (
    <>
      <div className="flex flex-col items-center gap-2.5 overflow-hidden rounded-lg bg-gradient-to-t from-dream-navy to-world-aa px-8 pb-11 md:pb-20 desktop:pt-20 desktop:pb-24">
        <AudioTitleMeta
          title={story.title}
          childName={story.childName}
          worldName={WORLD_NAME_BY_SLUG[worldSlug]}
          createdDate={formatDate(story.createdAt)}
          titleTestId="inlineplayer-title"
        />
        <AudioProgressBar className="md:max-w-[468px] desktop:max-w-[300px]" />
        <AudioBigControls />
        <AudioMiniControls onSchlafTimerClick={() => setSheetOpen(true)} />
      </div>
      <SchlafTimerSheet open={sheetOpen} onOpenChange={setSheetOpen} />
    </>
  );
}
